import { T } from 'gt-react';
import type { InterviewPhase } from '@erp/rpc';

/**
 * A labeled divider bar marking the start of a phase zone (setup / action / teardown).
 *
 * Shared by the live draft and the saved composition so a procedure reads the same way while it is
 * being authored as it does afterwards — which is the whole reason the two views exist.
 *
 * `as="li"` for the composed step list, which is an `<ol>`: a divider must sit inside it without
 * taking a step ordinal, so it renders as a non-interactive list item rather than breaking out of
 * the list.
 */
export function PhaseDivider({
  phase,
  as = 'div',
}: {
  phase: InterviewPhase;
  as?: 'div' | 'li';
}) {
  const Wrapper = as;
  return (
    <Wrapper
      className={
        as === 'li'
          ? 'flex list-none items-center gap-2 pt-1'
          : 'flex items-center gap-2'
      }
    >
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {phase === 'setup' ? (
          <T context="procedure zone header: the setup steps, which run first">
            Setup · runs first
          </T>
        ) : phase === 'teardown' ? (
          <T context="procedure zone header: the teardown steps, which run last">
            Teardown · runs last
          </T>
        ) : (
          <T context="procedure zone header: the repeatable main actions">
            Actions
          </T>
        )}
      </span>
      <div className="h-px flex-1 bg-border" />
    </Wrapper>
  );
}
